import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

interface BankAccount {
  id: string;
  institution_name: string;
  last4: string;
  account_type: string;
  status: string;
}

interface BankAccountsProps {
  refreshKey?: number;
}

export default function BankAccounts({ refreshKey = 0 }: BankAccountsProps) {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAccounts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const res = await fetch('/api/v1/bank/accounts', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      } else {
        console.error('Failed to load bank accounts:', res.status);
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {accounts.map((account) => {
        const statusColor = account.status === 'verified' 
          ? 'text-green-400 bg-green-400/10' 
          : account.status === 'pending'
          ? 'text-yellow-400 bg-yellow-400/10'
          : 'text-neutral-400 bg-neutral-400/10';

        const institutionIcon = account.institution_name.toLowerCase().includes('chase') ? '🏦'
          : account.institution_name.toLowerCase().includes('bank of america') ? '🏛️'
          : account.institution_name.toLowerCase().includes('wells') ? '🐴'
          : '🏢';

        return (
          <div
            key={account.id}
            className="rounded-xl border border-neutral-700 bg-neutral-800/50 p-4 hover:border-neutral-600 transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{institutionIcon}</div>
                <div>
                  <div className="font-medium text-white">{account.institution_name}</div>
                  <div className="text-sm text-neutral-400">
                    {account.account_type.charAt(0).toUpperCase() + account.account_type.slice(1)} ••••{account.last4}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
              </div>
            </div>
          </div>
        );
      })}

      {!accounts.length && (
        <div className="text-center py-12 px-4 border border-dashed border-neutral-700 rounded-xl">
          <div className="text-4xl mb-3">🏦</div>
          <div className="text-neutral-400 text-sm mb-1">No linked accounts yet</div>
          <div className="text-neutral-500 text-xs">Link a bank account to start funding your investments</div>
        </div>
      )}
    </div>
  );
}
